import {
  Badge,
  Dropdown,
  Flex,
  type MenuProps,
  Table,
  type TableProps,
  Typography,
} from 'antd';
import { type ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import IconArrowRight from '@/assets/icons/arrow-right.svg?react';
import IconMoreHoriz from '@/assets/icons/more-horiz.svg?react';
import { USER_ROLES_NAME } from '@/constants/auth.constants';
import {
  DATE_TIME_FORMAT_JP_WITHOUT_SECOND,
  TIME_ZONE,
} from '@/constants/datetime.constants';
import { ROUTES_PATH } from '@/constants/routes.constants';
import {
  DEFAULT_DROPDOWN_OPTIONS,
  DEFAULT_PAGINATION_OPTIONS,
} from '@/constants/table.constants';
import { useModalMethod } from '@/hooks/use-modal-method';
import { UserRoleEnum } from '@/types/auth.types';

import { ACCOUNT_STATUS_CONTENTS } from '../constants';
import { useDeleteAccount, useResendInvitationMail } from '../mutations';
import { type AccountResponseItem, AccountStatusEnum } from '../types';

const { Text } = Typography;

interface AccountTableProps extends TableProps<AccountResponseItem> {
  loginAccountId?: number;
  isWeddingChain?: boolean;
  isOrganizationAccount?: boolean;
  deleteAccount?: ReturnType<typeof useDeleteAccount>['mutate'];
  resendInvitationMail?: ReturnType<typeof useResendInvitationMail>['mutate'];
}

export function AccountTable({
  loginAccountId,
  isWeddingChain,
  isOrganizationAccount,
  deleteAccount,
  resendInvitationMail,
  ...rest
}: Readonly<AccountTableProps>) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { confirm } = useModalMethod();

  async function handleDeleteAccount(accountId: number) {
    const confirmed = await confirm({
      title: 'アカウントの削除',
      content: 'このアカウントを削除しますか？',
      okText: '削除',
    });
    if (!confirmed) return;
    deleteAccount?.(accountId);
  }

  function handleResendInvitationMail(accountId: number) {
    resendInvitationMail?.(String(accountId));
  }

  function handleNavigateAccountDetail(accountId: number) {
    const generatedPath = generatePath(
      id
        ? ROUTES_PATH.organizationAccountsDetail
        : accountId === loginAccountId
          ? ROUTES_PATH.myAccount
          : ROUTES_PATH.myOrganizationAccountsDetail,
      { id: id!, accountId: String(accountId) },
    );
    navigate(generatedPath, {
      state: {
        from: ROUTES_PATH.myOrganization,
        fromState: { activeKey: 'accounts' },
      },
    });
  }

  const items = (
    accountId: number,
    status: AccountResponseItem['status'],
  ): MenuProps['items'] => [
    {
      key: '0',
      label: (
        <Flex align="center" justify="space-between">
          <Text>
            {status === AccountStatusEnum['registration-complete']
              ? 'アカウントの編集'
              : '招待メールを再送信する'}
          </Text>
          <IconArrowRight className="ml-24" />
        </Flex>
      ),
      onClick: () =>
        status === AccountStatusEnum['registration-complete']
          ? handleNavigateAccountDetail(accountId)
          : handleResendInvitationMail(accountId),
    },
    ...(loginAccountId === accountId
      ? []
      : [
          {
            key: '1',
            label: (
              <Flex align="center" justify="space-between">
                <Text>アカウントの削除</Text>
                <IconArrowRight className="ml-24" />
              </Flex>
            ),
            onClick: () => handleDeleteAccount(accountId),
          },
        ]),
  ];

  const adminTypeColumn = {
    title: '権限',
    dataIndex: 'admin_type',
    width: isWeddingChain ? '11%' : '17%',
    ellipsis: true,
    render: (adminType: UserRoleEnum) => USER_ROLES_NAME[adminType],
  };

  const columns: ColumnsType<AccountResponseItem> = [
    {
      title: isOrganizationAccount ? 'アカウント' : 'メンバー',
      dataIndex: 'name',
      className: 'white-space-pre',
      width: isWeddingChain ? '24%' : '29%',
      ellipsis: true,
    },
    {
      title: 'メールアドレス',
      dataIndex: 'email',
      width: isWeddingChain ? '24%' : '29%',
      ellipsis: true,
    },
    ...(isOrganizationAccount
      ? ([
          ...(isWeddingChain ? [adminTypeColumn] : []),
          {
            title: '登録日時',
            dataIndex: 'created_at',
            width: '22%',
            ellipsis: true,
            render: createdAt =>
              dayjs(createdAt)
                .tz(TIME_ZONE)
                .format(DATE_TIME_FORMAT_JP_WITHOUT_SECOND),
          },
          {
            title: 'ステータス',
            dataIndex: 'status',
            width: '13%',
            ellipsis: true,
            render: (status: AccountStatusEnum) => (
              <Badge {...ACCOUNT_STATUS_CONTENTS[status]} />
            ),
          },
          {
            width: 60,
            align: 'center',
            className: 'py-0',
            render: (_, { id, status }) => (
              <Dropdown
                menu={{ items: items(id, status) }}
                {...DEFAULT_DROPDOWN_OPTIONS}
              >
                <IconMoreHoriz />
              </Dropdown>
            ),
          },
        ] as ColumnsType<AccountResponseItem>)
      : ([
          {
            title: '担当支社',
            width: '21%',
            ellipsis: true,
            render: ({
              admin_type: adminType,
              sale_areas: saleAreas,
            }: AccountResponseItem) =>
              [UserRoleEnum.sbf_master, UserRoleEnum.sbs_operation].includes(
                adminType,
              )
                ? 'すべて'
                : (saleAreas?.map(({ name }) => name)?.join('、') ?? '-'),
          },
          adminTypeColumn,
        ] as ColumnsType<AccountResponseItem>)),
  ];

  return (
    <Table
      rowKey="id"
      bordered
      columns={columns}
      {...rest}
      pagination={{ ...DEFAULT_PAGINATION_OPTIONS, ...rest.pagination }}
    />
  );
}
